import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import { useEffect, useRef, useState } from 'react';
import { promiseDeprecated, promiseDeprecatedFromClonedModel, promiseDeprecatedFromLocalService, resolveCatchError } from '../../core/constants';
import { isRaceError } from '../../core/genRaceQueue';
import { defaultReducer } from '../../core/glue';
import runtimeVar from '../../core/runtimeVar';
import { isAsync, isModel } from '../../tools';
export var runtimePromiseDeprecatedVarAssignment = function runtimePromiseDeprecatedVarAssignment(callback, flag) {
  runtimeVar.runtimePromiseDeprecatedFlag = flag;
  var result = callback();
  runtimeVar.runtimePromiseDeprecatedFlag = promiseDeprecated;
  return result;
};
export var isDeprecatedBySelf = function isDeprecatedBySelf(err, p, flags) {
  var isDeprecatedError = isRaceError(err);
  var deprecatedBySelf = false;
  var l = flags.length;
  for (var i = 0; i < l; i += 1) {
    deprecatedBySelf || (deprecatedBySelf = flags[i] in p);
  }
  return isDeprecatedError && deprecatedBySelf;
};
var useCloneModel = function useCloneModel(model, mutedCallback, options) {
  var _ref = options || {},
    control = _ref.control;
  var unmountedFlagRef = useRef(false);
  var cacheControlRef = useRef();
  var cacheControlOnChangeUnsub = useRef();
  var cacheModelRef = useRef();
  var underControl = useRef(false);
  var _useState = useState(function () {
      if (isModel(control)) {
        underControl.current = true;
        var r = control();
        if (r.successful) {
          model.silent(r.data);
        }
        return {
          loading: r.loading,
          successful: r.successful
        };
      }
      return {
        loading: false,
        successful: false
      };
    }),
    _useState2 = _slicedToArray(_useState, 2),
    status = _useState2[0],
    updateStatus = _useState2[1];
  var syncUpdateStatus = function syncUpdateStatus(s) {
    updateStatus(function (prevState) {
      if (prevState.loading === s.loading && prevState.successful === s.successful) return prevState;
      return _objectSpread(_objectSpread({}, prevState), s);
    });
  };
  if (cacheControlRef.current !== control && isModel(control) && underControl.current) {
    cacheControlRef.current = control;
    if (cacheControlOnChangeUnsub.current) {
      cacheControlOnChangeUnsub.current();
    }
    cacheControlOnChangeUnsub.current = control.onChange(function (state) {
      if (state.successful) {
        model.silent(state.data);
      }
      syncUpdateStatus({
        loading: state.loading,
        successful: state.successful,
        error: state.error
      });
    });
  }
  var genClonedModel = function genClonedModel() {
    var statusHandleFn = function statusHandleFn(p, async) {
      underControl.current = false;
      cacheControlRef.current = undefined;
      if (cacheControlOnChangeUnsub.current) {
        cacheControlOnChangeUnsub.current();
        cacheControlOnChangeUnsub.current = undefined;
      }
      if (!async) {
        syncUpdateStatus({
          loading: false,
          successful: false,
          error: null
        });
        return p;
      }
      syncUpdateStatus({
        loading: true,
        successful: false,
        error: null
      });
      p.catch(function (err) {
        if (unmountedFlagRef.current) return resolveCatchError;
        if (!isRaceError(err) || !isDeprecatedBySelf(err, p, [promiseDeprecatedFromClonedModel, promiseDeprecatedFromLocalService])) {
          syncUpdateStatus({
            loading: false,
            successful: false,
            error: isRaceError(err) ? null : err
          });
        }
        return resolveCatchError;
      }).then(function (info) {
        if (unmountedFlagRef.current || info === resolveCatchError) return;
        syncUpdateStatus({
          successful: true,
          loading: false,
          error: null
        });
      });
      return p;
    };
    var fn = function fn() {
      var res = model.preTreat.apply(model, arguments);
      if (arguments.length === 0) return res;
      if (isAsync(res)) {
        return statusHandleFn(model(res, defaultReducer, (arguments.length <= 2 ? undefined : arguments[2]) || mutedCallback), true);
      }
      return model(res);
    };
    Object.setPrototypeOf(fn, model);
    fn.race = function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      var r = model.preTreat.apply(model, args);
      return runtimePromiseDeprecatedVarAssignment(function () {
        return statusHandleFn(model.race(r, defaultReducer, args[2] || mutedCallback), isAsync(r));
      }, promiseDeprecatedFromClonedModel);
    };
    fn.__race__ = function () {
      var r = model.preTreat.apply(model, arguments);
      return statusHandleFn(model.race(r, defaultReducer, (arguments.length <= 2 ? undefined : arguments[2]) || mutedCallback), isAsync(r));
    };
    return fn;
  };
  var _useState3 = useState(function () {
      return genClonedModel();
    }),
    _useState4 = _slicedToArray(_useState3, 1),
    clonedModel = _useState4[0];
  var cacheClonedModelRef = useRef(clonedModel);
  if (!Object.is(model, cacheModelRef.current)) {
    cacheModelRef.current = model;
    cacheClonedModelRef.current = genClonedModel();
  }
  useEffect(function () {
    return function () {
      var _cacheControlOnChange;
      unmountedFlagRef.current = true;
      (_cacheControlOnChange = cacheControlOnChangeUnsub.current) === null || _cacheControlOnChange === void 0 ? void 0 : _cacheControlOnChange.call(cacheControlOnChangeUnsub);
    };
  }, []);
  return [cacheClonedModelRef.current, _objectSpread({}, status)];
};
export default useCloneModel;